import { AxiosError } from 'axios';
import dayjs from 'dayjs';

import { SCUD_EMPTY, SCUD_SERVICE_UNAVAILABLE_TEXT } from '@/shared/constant/scudMapping';
import { getRequestErrorText } from '@/shared/helpers/requestError';
import { IRequestError } from '@/shared/types/app.types';
import {
	IScudFederationPerson,
	IScudMapping,
	IScudMappingReason,
} from '@/shared/types/scudMapping.types';

export const formatScudDate = (value: string | null | undefined) =>
	value ? dayjs(value).format('DD.MM.YYYY') : SCUD_EMPTY;

/** Дата приёма/увольнения приходит с поясом «Федерации»: день берём из строки, а не из пояса браузера */
export const formatScudCalendarDate = (value: string | null | undefined) =>
	value ? dayjs(value.slice(0, 10)).format('DD.MM.YYYY') : SCUD_EMPTY;

export const formatScudDateTime = (value: string | null | undefined) =>
	value ? dayjs(value).format('DD.MM.YYYY HH:mm') : SCUD_EMPTY;

export const joinParts = (...parts: Array<string | null | undefined>) =>
	parts.filter((part) => part && part.trim()).join(' · ');

export const employeeDetails = (employee: IScudFederationPerson | null | undefined) =>
	employee
		? joinParts(
				employee.company_name,
				employee.unit_name,
				employee.position,
				employee.tab_number ? `таб. № ${employee.tab_number}` : null,
			)
		: '';

/** Ключ пропуска: в Болиде — tab_number, в КОДОС — person_id_skud (ТЗ 8.2) */
export const scudPassNumber = (mapping: Pick<IScudMapping, 'person_id_skud' | 'tab_number'>) =>
	mapping.tab_number || mapping.person_id_skud || SCUD_EMPTY;

/** Снятая с использования причина остаётся в старых записях с пометкой */
export const reasonLabel = (reason: Pick<IScudMappingReason, 'name' | 'is_active'>) =>
	reason.is_active ? reason.name : `${reason.name} (не используется)`;

/** Подписи причин по id; если причины нет в справочнике — название, пришедшее с записью */
export const reasonLabelById = (
	reasons: IScudMappingReason[],
	id: string | null | undefined,
	fallback?: string | null,
) => {
	const reason = reasons.find((item) => item.id === id);

	return reason ? reasonLabel(reason) : (fallback ?? null);
};

/** 502/503: message Бронирования показываем как есть; от прокси тело пустое или не JSON */
export const requestErrorText = (error: unknown, action: string) => {
	const response = (error as AxiosError<IRequestError | string>)?.response;

	if (response?.status === 502 || response?.status === 503) {
		const message =
			response.data && typeof response.data === 'object' ? response.data.message?.trim() : '';

		return message || `${action}. ${SCUD_SERVICE_UNAVAILABLE_TEXT}.`;
	}

	return getRequestErrorText(error as AxiosError<IRequestError>, action);
};

/** 409 — запись изменил другой пользователь или такая запись уже есть */
export const isConflict = (error: unknown) => (error as AxiosError)?.response?.status === 409;
